import { useMutation } from '@apollo/client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  SimpleTooltip,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const SetTenantUserExcludedMutation = graphql(`
  mutation SetAdminTenantUserExcluded(
    $tenantId: uuid!
    $userId: uuid!
    $isExcluded: Boolean!
  ) {
    updateTenantUsersByPk(
      pkColumns: { tenantId: $tenantId, userId: $userId }
      _set: { isExcluded: $isExcluded }
    ) {
      __typename
      tenantId
      userId
      isExcluded
    }
  }
`);

interface UserExcludeButtonProps {
  userId: string;
  tenantId: string;
  isExcluded: boolean;
}

export const UserExcludeButton: React.FC<UserExcludeButtonProps> = ({
  userId,
  tenantId,
  isExcluded,
}) => {
  const toast = useToast();
  const [setUserExcluded] = useMutation(SetTenantUserExcludedMutation, {
    refetchQueries: ['GetTenantUsers'],
    onError: () => {
      toast.error(
        isExcluded
          ? 'Failed to include user in usage reports'
          : 'Failed to exclude user from usage reports',
      );
    },
  });

  const handleSetExcluded = (excluded: boolean) => {
    setUserExcluded({
      variables: { tenantId, userId, isExcluded: excluded },
    });
  };

  if (isExcluded) {
    return (
      <SimpleTooltip label="Include in usage reports">
        <Button variant="outline" onClick={() => handleSetExcluded(false)}>
          <Icon name="Eye" size="xl" />
        </Button>
      </SimpleTooltip>
    );
  }

  return (
    <AlertDialog>
      <SimpleTooltip label="Exclude from usage reports">
        <AlertDialogTrigger asChild>
          <Button variant="outline">
            <Icon name="EyeOff" size="xl" />
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This user will be hidden from this list and won't count toward usage
            reports.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleSetExcluded(true)}>
            Exclude User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
